
import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useCategoryPreferences, AVAILABLE_CATEGORIES } from "@/hooks/useCategoryPreferences";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Globe, User, Mail, Phone, Heart, 
  Calendar, MapPin, ExternalLink, LogOut, Edit2, 
  Save, X, Loader2, Sparkles, Star, Bell, Check,
  Home, Settings, ChevronLeft, ChevronRight
} from "lucide-react";

interface VisitorProfile {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
}

interface FavoriteEvent {
  id: string;
  event_id: string;
  created_at: string;
  event: {
    id: string;
    title: string;
    title_ar: string | null;
    date: string | null;
    location: string | null;
    location_ar: string | null;
    image_url: string | null;
    external_link: string | null;
  } | null;
}

type ActiveTab = "profile" | "notifications" | "favorites";

const Profile = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const navigate = useNavigate();
  const { user, loading: authLoading, signOut } = useAuth();
  const { preferences, toggleCategory, isSubscribed, loading: prefsLoading } = useCategoryPreferences();

  const [profile, setProfile] = useState<VisitorProfile | null>(null);
  const [favorites, setFavorites] = useState<FavoriteEvent[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({ full_name: "", phone: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<ActiveTab>("profile");

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchProfile();
      fetchFavorites();
    }
  }, [user]);

  const fetchProfile = async () => {
    if (!user) return;
    
    const { data, error } = await supabase
      .from("visitors")
      .select("*")
      .eq("user_id", user.id)
      .single();

    if (error) {
      console.error("Error fetching profile:", error);
      if (error.code === "PGRST116") {
        const { data: newProfile, error: createError } = await supabase
          .from("visitors")
          .insert({
            user_id: user.id,
            full_name: user.user_metadata?.full_name || user.email?.split("@")[0] || "User",
            email: user.email || "",
          })
          .select()
          .single();
        
        if (!createError && newProfile) {
          setProfile(newProfile);
          setEditData({ full_name: newProfile.full_name, phone: newProfile.phone || "" });
        }
      }
    } else if (data) {
      setProfile(data);
      setEditData({ full_name: data.full_name, phone: data.phone || "" });
    }
    setLoading(false);
  };

  const fetchFavorites = async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from("favorite_events")
      .select(`
        id,
        event_id,
        created_at,
        event:scraped_events(id, title, title_ar, date, location, location_ar, image_url, external_link)
      `)
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching favorites:", error);
    } else {
      setFavorites(data as unknown as FavoriteEvent[]);
    }
  };

  const handleSaveProfile = async () => {
    if (!user || !profile) return;
    setSaving(true);

    const { error } = await supabase
      .from("visitors")
      .update({
        full_name: editData.full_name,
        phone: editData.phone || null,
      })
      .eq("user_id", user.id);

    if (error) {
      toast.error(isRTL ? "حدث خطأ في الحفظ" : "Error saving profile");
    } else {
      setProfile({ ...profile, full_name: editData.full_name, phone: editData.phone || null });
      setIsEditing(false);
      toast.success(isRTL ? "تم حفظ التغييرات بنجاح" : "Changes saved successfully");
    }
    setSaving(false);
  };

  const handleRemoveFavorite = async (favoriteId: string) => {
    const { error } = await supabase
      .from("favorite_events")
      .delete()
      .eq("id", favoriteId);

    if (error) {
      toast.error(isRTL ? "حدث خطأ في الحذف" : "Error removing favorite");
    } else {
      setFavorites(favorites.filter((f) => f.id !== favoriteId));
      toast.success(isRTL ? "تم إزالة الفعالية من المفضلة" : "Event removed from favorites");
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate("/");
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1a0f22] via-[#241530] to-[#1a0f22]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const sidebarItems = [
    { key: "profile" as ActiveTab, icon: User, label: { ar: "معلومات الحساب", en: "Account Info" } },
    { key: "notifications" as ActiveTab, icon: Bell, label: { ar: "الإشعارات", en: "Notifications" } },
    { key: "favorites" as ActiveTab, icon: Heart, label: { ar: "المفضلة", en: "Favorites" }, badge: favorites.length > 0 ? favorites.length : undefined },
  ];

  const statsCards = [
    { icon: Heart, label: { ar: "المفضلة", en: "Favorites" }, value: favorites.length.toString(), color: "text-red-400" },
    { icon: Bell, label: { ar: "الاشتراكات", en: "Subscriptions" }, value: preferences.length.toString(), color: "text-yellow-400" },
    { icon: Star, label: { ar: "الحالة", en: "Status" }, value: isRTL ? "نشط" : "Active", color: "text-green-400" },
  ];

  return (
    <div className={`min-h-screen relative overflow-hidden ${isRTL ? "rtl" : "ltr"}`}>
      {/* Dark Premium Background */}
      <div className="fixed inset-0 bg-gradient-to-br from-[#1a0f22] via-[#241530] to-[#1a0f22] -z-10" />
      
      {/* Islamic Geometric Pattern */}
      <div className="fixed inset-0 opacity-5 -z-10">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="profilePattern" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
              <path d="M30 0L60 30L30 60L0 30Z" fill="none" stroke="white" strokeWidth="0.5"/>
              <circle cx="30" cy="30" r="10" fill="none" stroke="white" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#profilePattern)" />
        </svg>
      </div>

      {/* Glowing Orbs */}
      <div className="fixed top-0 left-1/4 w-96 h-96 bg-primary/15 rounded-full blur-3xl -z-10" />
      <div className="fixed bottom-0 right-1/4 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl -z-10" />

      {/* Top Header Bar */}
      <header className="bg-gray-900/80 border-b border-white/10 sticky top-0 z-50">
        <div className="px-4 md:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm">
              <Home className="w-4 h-4" />
              {isRTL ? "الرئيسية" : "Home"}
            </Link>
            <span className="text-white/30">/</span>
            <span className="text-white text-sm font-medium">{isRTL ? "لوحة التحكم" : "Dashboard"}</span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => i18n.changeLanguage(i18n.language === "en" ? "ar" : "en")}
              className="text-white/70 hover:text-white hover:bg-white/10 rounded-lg"
            >
              <Globe className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-white/70 hover:text-white hover:bg-white/10 rounded-lg flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">{isRTL ? "خروج" : "Logout"}</span>
            </Button>
          </div>
        </div>
      </header>

      {/* User Info Banner */}
      <div className="bg-gray-800/50 border-b border-white/10 px-4 md:px-8 py-5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-primary/20 border-2 border-primary/40 flex items-center justify-center shrink-0">
            <User className="w-8 h-8 text-primary-foreground" />
          </div>
          <div className="text-center sm:text-start flex-1 min-w-0">
            <h1 className="text-xl md:text-2xl font-bold text-white truncate">
              {profile?.full_name || (isRTL ? "المستخدم" : "User")}
            </h1>
            <p className="text-white/50 text-sm truncate">{profile?.email}</p>
          </div>
          {/* Stats Cards */}
          <div className="flex gap-3 flex-wrap justify-center sm:justify-end">
            {statsCards.map((stat, idx) => (
              <div key={idx} className="bg-white/[0.08] border border-white/[0.12] rounded-xl px-4 py-3 text-center min-w-[100px]">
                <stat.icon className={`w-5 h-5 mx-auto mb-1 ${stat.color}`} />
                <div className="text-lg font-bold text-white">{stat.value}</div>
                <div className="text-xs text-white/50">{isRTL ? stat.label.ar : stat.label.en}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Layout: Sidebar + Content */}
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-6 px-4 md:px-8 py-6">
        {/* Sidebar */}
        <aside className="md:w-64 shrink-0">
          <nav className="bg-white/[0.08] border border-white/[0.12] rounded-xl overflow-hidden">
            {sidebarItems.map((item) => (
              <button
                key={item.key}
                onClick={() => setActiveTab(item.key)}
                className={`w-full flex items-center gap-3 px-5 py-4 text-sm transition-all border-b border-white/[0.06] last:border-b-0 ${
                  activeTab === item.key
                    ? "bg-primary/20 text-primary-foreground border-l-4 border-l-primary"
                    : "text-white/60 hover:bg-white/[0.05] hover:text-white"
                }`}
              >
                <item.icon className="w-5 h-5 shrink-0" />
                <span className="flex-1 text-start">{isRTL ? item.label.ar : item.label.en}</span>
                {item.badge && (
                  <span className="bg-primary/30 text-primary-foreground text-xs px-2 py-0.5 rounded-full">
                    {item.badge}
                  </span>
                )}
                {isRTL ? <ChevronLeft className="w-4 h-4 opacity-40" /> : <ChevronRight className="w-4 h-4 opacity-40" />}
              </button>
            ))}
          </nav>
        </aside>

        {/* Content Area */}
        <main className="flex-1 min-w-0">
          <AnimatePresence mode="wait">
            {/* Profile Tab */}
            {activeTab === "profile" && (
              <motion.div
                key="profile"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                <div className="bg-white/[0.08] border border-white/[0.12] rounded-xl overflow-hidden">
                  <div className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
                    <h2 className="text-white font-semibold flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-primary-foreground" />
                      {isRTL ? "معلومات الحساب" : "Account Information"}
                    </h2>
                    {!isEditing && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsEditing(true)}
                        className="text-primary-foreground hover:bg-white/10 flex items-center gap-2"
                      >
                        <Edit2 className="w-4 h-4" />
                        {isRTL ? "تعديل" : "Edit"}
                      </Button>
                    )}
                  </div>
                  <div className="p-6 space-y-5">
                    {/* Full Name */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 text-white/60 text-sm">
                        <User className="w-4 h-4" />
                        {isRTL ? "الاسم الكامل" : "Full Name"}
                      </Label>
                      {isEditing ? (
                        <Input
                          value={editData.full_name}
                          onChange={(e) => setEditData({ ...editData, full_name: e.target.value })}
                          className="bg-white/[0.06] border-white/[0.12] text-white rounded-lg"
                        />
                      ) : (
                        <p className="text-white font-medium bg-white/[0.04] border border-white/[0.08] rounded-lg p-3">
                          {profile?.full_name}
                        </p>
                      )}
                    </div>

                    {/* Email */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 text-white/60 text-sm">
                        <Mail className="w-4 h-4" />
                        {isRTL ? "البريد الإلكتروني" : "Email"}
                      </Label>
                      <p className="text-white font-medium bg-white/[0.04] border border-white/[0.08] rounded-lg p-3">
                        {profile?.email}
                      </p>
                    </div>

                    {/* Phone */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 text-white/60 text-sm">
                        <Phone className="w-4 h-4" />
                        {isRTL ? "رقم الهاتف" : "Phone Number"}
                      </Label>
                      {isEditing ? (
                        <Input
                          value={editData.phone}
                          onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                          className="bg-white/[0.06] border-white/[0.12] text-white rounded-lg"
                          placeholder={isRTL ? "أدخل رقم الهاتف" : "Enter phone number"}
                        />
                      ) : (
                        <p className="text-white font-medium bg-white/[0.04] border border-white/[0.08] rounded-lg p-3">
                          {profile?.phone || (isRTL ? "غير محدد" : "Not specified")}
                        </p>
                      )}
                    </div>

                    {isEditing && (
                      <div className="flex gap-3 pt-4">
                        <Button
                          onClick={handleSaveProfile}
                          disabled={saving}
                          className="flex-1 rounded-lg"
                        >
                          {saving ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Save className="w-4 h-4 mr-2" />
                              {isRTL ? "حفظ" : "Save"}
                            </>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setIsEditing(false);
                            setEditData({ full_name: profile?.full_name || "", phone: profile?.phone || "" });
                          }}
                          className="flex-1 rounded-lg border-white/20 text-white hover:bg-white/10"
                        >
                          <X className="w-4 h-4 mr-2" />
                          {isRTL ? "إلغاء" : "Cancel"}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Notifications Tab */}
            {activeTab === "notifications" && (
              <motion.div
                key="notifications"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                <div className="bg-white/[0.08] border border-white/[0.12] rounded-xl overflow-hidden">
                  <div className="border-b border-white/10 px-6 py-4">
                    <h2 className="text-white font-semibold flex items-center gap-2">
                      <Bell className="w-5 h-5 text-primary-foreground" />
                      {isRTL ? "تفضيلات الإشعارات" : "Notification Preferences"}
                    </h2>
                    <p className="text-white/40 text-sm mt-1">
                      {isRTL 
                        ? "اختر الفئات التي تريد تلقي إشعارات عند إضافة فعاليات جديدة فيها"
                        : "Select categories to receive notifications when new events are added"}
                    </p>
                  </div>
                  <div className="p-4 space-y-2">
                    {AVAILABLE_CATEGORIES.map((category, index) => (
                      <motion.div
                        key={category.key}
                        initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.2, delay: index * 0.03 }}
                      >
                        <button
                          onClick={() => toggleCategory(category.key, isRTL)}
                          className={`w-full flex items-center justify-between p-4 rounded-lg transition-all duration-200 ${
                            isSubscribed(category.key)
                              ? "bg-primary/15 border border-primary/30"
                              : "bg-white/[0.04] border border-white/[0.08] hover:bg-white/[0.08]"
                          }`}
                        >
                          <span className="font-medium text-white">
                            {isRTL ? category.name_ar : category.name_en}
                          </span>
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                            isSubscribed(category.key)
                              ? "bg-primary text-primary-foreground"
                              : "bg-white/10"
                          }`}>
                            {isSubscribed(category.key) && <Check className="w-4 h-4" />}
                          </div>
                        </button>
                      </motion.div>
                    ))}
                    
                    {preferences.length > 0 && (
                      <div className="pt-4 border-t border-white/10 mt-4">
                        <p className="text-sm text-white/40 text-center">
                          {isRTL 
                            ? `مشترك في ${preferences.length} ${preferences.length === 1 ? "فئة" : "فئات"}`
                            : `Subscribed to ${preferences.length} ${preferences.length === 1 ? "category" : "categories"}`}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Favorites Tab */}
            {activeTab === "favorites" && (
              <motion.div
                key="favorites"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                <div className="bg-white/[0.08] border border-white/[0.12] rounded-xl overflow-hidden">
                  <div className="border-b border-white/10 px-6 py-4">
                    <h2 className="text-white font-semibold flex items-center gap-2">
                      <Heart className="w-5 h-5 text-red-400" />
                      {isRTL ? `المفضلة (${favorites.length} فعالية)` : `Favorites (${favorites.length} events)`}
                    </h2>
                  </div>

                  {favorites.length === 0 ? (
                    <div className="p-12 text-center">
                      <Heart className="w-16 h-16 mx-auto text-white/20 mb-4" />
                      <h3 className="text-lg font-semibold text-white/60 mb-2">
                        {isRTL ? "لا توجد فعاليات مفضلة" : "No favorite events"}
                      </h3>
                      <p className="text-white/40 mb-6">
                        {isRTL 
                          ? "أضف فعاليات للمفضلة لتراها هنا" 
                          : "Add events to favorites to see them here"}
                      </p>
                      <Button asChild className="rounded-lg">
                        <Link to="/">{isRTL ? "تصفح الفعاليات" : "Browse Events"}</Link>
                      </Button>
                    </div>
                  ) : (
                    <div className="divide-y divide-white/[0.06]">
                      <AnimatePresence>
                        {favorites.map((favorite, index) => (
                          <motion.div
                            key={favorite.id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2, delay: index * 0.05 }}
                            className="flex items-center gap-4 p-4 hover:bg-white/[0.04] transition-colors"
                          >
                            {/* Event Image */}
                            {favorite.event?.image_url ? (
                              <img
                                src={favorite.event.image_url}
                                alt={isRTL ? favorite.event.title_ar || favorite.event.title : favorite.event.title}
                                className="w-16 h-16 rounded-lg object-cover shrink-0"
                              />
                            ) : (
                              <div className="w-16 h-16 rounded-lg bg-white/[0.06] flex items-center justify-center shrink-0">
                                <Calendar className="w-6 h-6 text-white/30" />
                              </div>
                            )}

                            {/* Event Info */}
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-white truncate">
                                {isRTL 
                                  ? favorite.event?.title_ar || favorite.event?.title 
                                  : favorite.event?.title}
                              </h3>
                              <div className="flex items-center gap-3 mt-1">
                                {favorite.event?.date && (
                                  <span className="flex items-center gap-1 text-white/50 text-xs">
                                    <Calendar className="w-3 h-3" />
                                    {favorite.event.date}
                                  </span>
                                )}
                                {(favorite.event?.location || favorite.event?.location_ar) && (
                                  <span className="flex items-center gap-1 text-white/50 text-xs truncate">
                                    <MapPin className="w-3 h-3" />
                                    {isRTL 
                                      ? favorite.event?.location_ar || favorite.event?.location 
                                      : favorite.event?.location}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-2 shrink-0">
                              {favorite.event?.external_link && (
                                <a
                                  href={favorite.event.external_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-2 rounded-lg bg-primary/20 text-primary-foreground hover:bg-primary/30 transition-colors"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </a>
                              )}
                              <button
                                onClick={() => handleRemoveFavorite(favorite.id)}
                                className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
};

export default Profile;
